import React from 'react';
import { getOTRateLabel } from '@medely/base';
import { Box, Icon, InfoBadge, Text, VStack } from '@medely/ui-kit';
import { JobDetailsListItem } from '@mp/components/jobs';
import { HelpCenterLink } from '@mp/components/helpCenterLink';
import { AssignmentForAssignmentDetailsGql } from '@mp/components/assignmentDetails/graphql';
import { FragmentType, parseFragmentData } from '@mp/graphql/generated';
import { JobDetailsFragment } from '../jobs/jobDetailsComponents/graphql';
import { PayoutKind } from '@mp/utils/payoutUtils';

const MAX_HOURS_IN_WEEK = 168;
const MAX_HOURS_IN_DAY = 24;
const MIN_ALLOWED_WEEKLY_HOURS = 40;

type RateProfile = {
  payout_double_overtime_multiplier?: number | null;
  payout_kind: string;
  payout_max_daily_ot_hours?: number | null;
  payout_max_daily_regular_hours?: number | null;
  payout_max_weekly_regular_hours?: number | null;
  payout_overtime_multiplier?: number | null;
};

type ExtendedRateInfoProps = {
  job?: FragmentType<typeof JobDetailsFragment> | null;
  assignment?: FragmentType<typeof AssignmentForAssignmentDetailsGql> | null;
  smallIcon?: boolean;
  icon?: React.ReactNode;
  iconSize?: `${number}px`;
};

export const ExtendedRateInfo = ({
  job,
  assignment,
  smallIcon = false,
  icon,
  iconSize,
}: ExtendedRateInfoProps) => {
  let profile: RateProfile | null = null;
  const isJob = !!job;

  if (isJob) {
    const parsedJob = parseFragmentData(JobDetailsFragment, job);
    profile = parsedJob?.job_profile_for_account_id ?? null;
  } else {
    const parsedAssignment = parseFragmentData(AssignmentForAssignmentDetailsGql, assignment);
    profile = parsedAssignment?.assignment_profile_for_account_id ?? null;
  }

  if (!profile) {
    return null;
  }

  const is12HrFlatRate = profile.payout_max_daily_regular_hours === 12;
  const isW2 = profile.payout_kind === PayoutKind.W2;

  const rateLabel = getOTRateLabel({
    isW2: isW2,
    abbreviate: false,
    capitalize: false,
  });

  const dailyRateSentence = getDailyRateSentence(rateLabel, profile, isJob);
  const weeklyRateSentence = getWeeklyRateSentence(
    !!dailyRateSentence,
    rateLabel,
    profile,
    isJob,
    isW2,
  );
  const show1099HelpLink = profile?.payout_kind === PayoutKind.Ten99;

  const resolvedIconSize: `${number}px` = iconSize ?? (smallIcon ? '14px' : '20px');
  const iconElement = React.isValidElement(icon) ? (
    icon
  ) : (
    <Icon
      name="light-calendar-clock"
      iconSize={resolvedIconSize}
      color="text.primary"
      variant="custom"
    />
  );

  return (
    <JobDetailsListItem
      icon={iconElement}
      title={`${getOTRateLabel({
        isW2: isW2,
        abbreviate: false,
      })}s`}
      small={smallIcon}
    >
      <VStack flex={1}>
        {dailyRateSentence && <Text size="m">{dailyRateSentence}</Text>}
        {weeklyRateSentence && <Text size="m">{weeklyRateSentence}</Text>}
        {show1099HelpLink && (
          <HelpCenterLink configKey="weekly1099ExtendedRate" text="Learn more" />
        )}
        {is12HrFlatRate && (
          <Box alignSelf="flex-start">
            <InfoBadge label="12 hr flat rate" color="info" />
          </Box>
        )}
      </VStack>
    </JobDetailsListItem>
  );
};

const getDailyRateSentence = (rateLabel: string, profile: RateProfile, isJob: boolean) => {
  const hasDailyXR1 = (() => {
    const rules = {
      payout_max_daily_regular_hours: (hours: number | null | undefined) =>
        hours !== null && hours !== undefined && hours < MAX_HOURS_IN_DAY,
      payout_max_daily_ot_hours: (hours: number | null | undefined) =>
        hours !== null && hours !== undefined && hours > 0,
      payout_overtime_multiplier: (multiplier: number | null | undefined) =>
        multiplier !== null && multiplier !== undefined && multiplier !== 1,
    };

    return Object.entries(rules).every(([key, validate]) => {
      const value = profile[key as keyof typeof rules];
      return validate(value);
    });
  })();

  const hoursBeforeXR2 =
    (profile.payout_max_daily_regular_hours ?? 0) + (profile.payout_max_daily_ot_hours ?? 0);
  const hasXR2 =
    hoursBeforeXR2 < MAX_HOURS_IN_DAY && profile.payout_double_overtime_multiplier !== 1;

  const shiftText = isJob ? 'for this' : 'per';

  if (hasDailyXR1 && hasXR2) {
    // Both XR1 and XR2 (e.g., 8 hour rates in CA)
    return (
      `${profile.payout_overtime_multiplier}x daily ${rateLabel} applies for hours ` +
      `${profile.payout_max_daily_regular_hours} - ${hoursBeforeXR2} and ` +
      `${profile.payout_double_overtime_multiplier}x for hours after ${hoursBeforeXR2} ${shiftText} shift.`
    );
  }

  if (hasDailyXR1) {
    // Only XR1 (e.g., Colorado, Alaska)
    return (
      `${profile.payout_overtime_multiplier}x daily ${rateLabel} applies after ` +
      `${profile.payout_max_daily_regular_hours} hours ${shiftText} shift.`
    );
  }

  if (hasXR2) {
    // Only XR2 (e.g., 12 hour rates in CA)
    return (
      `${profile.payout_double_overtime_multiplier}x daily ${rateLabel} applies after ` +
      `${hoursBeforeXR2} hours ${shiftText} shift.`
    );
  }

  return null;
};

const getWeeklyRateSentence = (
  hasDailyRate: boolean,
  rateLabel: string,
  profile: RateProfile,
  isJob: boolean,
  isW2: boolean,
) => {
  const hasWeeklyXR = (() => {
    const rules = {
      payout_max_weekly_regular_hours: (hours: number | null | undefined) =>
        hours !== null &&
        hours !== undefined &&
        hours < MAX_HOURS_IN_WEEK &&
        hours >= MIN_ALLOWED_WEEKLY_HOURS,
      payout_overtime_multiplier: (multiplier: number | null | undefined) =>
        multiplier !== null && multiplier !== undefined && multiplier !== 1,
    };

    return Object.entries(rules).every(([key, validate]) => {
      const value = profile[key as keyof typeof rules];
      return validate(value);
    });
  })();

  if (!hasWeeklyXR) {
    return null;
  }

  const facilityText = isW2 || !isJob ? '' : ' at the same facility';

  if (hasDailyRate) {
    // Both daily and weekly XR. Shortened sentence.
    return (
      `${profile.payout_overtime_multiplier}x weekly ${rateLabel} applies after ` +
      `${profile.payout_max_weekly_regular_hours} regular hours${facilityText}.`
    );
  }

  return (
    // Only weekly XR. Longer sentence.
    `${profile.payout_overtime_multiplier}x weekly ${rateLabel} applies after ` +
    `${profile.payout_max_weekly_regular_hours} regular hours${facilityText} in one calendar week.`
  );
};
